import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePagination, useUpdateQueryParams } from 'vopay';
import { useToast } from '#shared/providers/Toast';
import { useDialog } from '#shared/providers/Dialog';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import useSubAccounts from '#shared/hooks/useApi/useSubAccounts';
import useBlockAccounts from '#shared/hooks/useApi/useBlockAccounts';
import SubAccountListProvider from './SubAccountList.provider';
import SubAccountListTable from './SubAccountListTable';
import { SubAccountListContextValue } from './SubAccountListContext';
import { SubAccountStatus } from './SubAccountSearchForm';
import { BlockDialog, UnBlockDialog, BlockAccountDialog, UnBlockAccountDialog } from './BlockingDialog';
import { mapBalances } from './subAccountRows';
import { SubAccountListHostProps, SubAccountListState } from './types';

type QueryParams = {
  status: string;
  currency: string;
  country: string;
  searchTerm: string;
};

export type SubAccountListProps = SubAccountListHostProps & {
  /** The host's page around the list; it mounts <SubAccountList.Table /> where the list belongs. */
  children: (state: SubAccountListState) => ReactNode;
};

// The Sub-Account List feature root (ID-13193). A pure move of client-dashboard's /sub-accounts-list page
// into shared. Owns what the old page held above its Card: the URL-driven filters, the pagination, the one
// list fetch, and the block / unblock flows — because the host's page header (Block Sub-Accounts button,
// blocked banner) and org-chart view read the same list, and the table part is unmounted in org-chart view
// without refetching on the way back. Hands the host-facing slice back through render-prop children and the
// rest to <SubAccountList.Table /> through the feature-local provider.
export default function SubAccountList({
  account,
  userPermissions,
  hostPaths,
  hostActions,
  permissionsEditor,
  children,
}: SubAccountListProps) {
  const { subAccounts, pagination: paginationMeta, fetchSubAccounts } = useSubAccounts();
  const { blockAccounts, unblockAccounts, blockAccount, unblockAccount } = useBlockAccounts();
  const toast = useToast();
  const dialogue = useDialog();
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.SubAccountList' });
  // Drives the table's skeleton. Local rather than the hook's fetchingSubAccounts so only the filter /
  // page fetch below shows it: the post-mutation refetches run under the blocking loader and must not
  // unmount the table (that would collapse every expanded row).
  const [isLoadingSubAccounts, setIsLoadingSubAccounts] = useState(true);

  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const status: SubAccountStatus = useMemo(() => {
    const param = queryParams.get('status');
    if (param === null) return 'Active'; // no param = default to Active on initial load
    if (param === 'NotActive') return 'NotActive';
    if (param === 'Active') return 'Active';

    // 'all' (the search-clear sentinel) and any other value mean "show every status"
    return '';
  }, [queryParams]);

  const currency = useMemo(() => queryParams.get('currency') || '', [queryParams]);
  const country = useMemo(() => queryParams.get('country') || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.get('searchTerm') || '', [queryParams]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const { pageCount, pageIndex, setPageIndex, offset } = usePagination(paginationMeta.TotalRecords || 0, {
    pageSize: 20,
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingSubAccounts(true);

      try {
        await fetchSubAccounts({
          PortalUser: account.accountID,
          ShowToken: true,
          SearchText: searchTerm || undefined,
          ActiveOnly: status === 'Active' ? true : status === 'NotActive' ? false : undefined,
          Currency: currency || undefined,
          Country: country || undefined,
          Limit: 20,
          Offset: offset,
        });
      } catch (error) {
        // The hook already logged; surface the server's message to the user, as the old page did
        // (a non-blocking toast — see client-dashboard SubAccountsPage).
        if (error instanceof Error) toast.neutral(error.message);
      } finally {
        setIsLoadingSubAccounts(false);
      }
    };

    fetchData();
  }, [status, currency, country, searchTerm, offset]);

  const search = ({
    searchTerm: newSearchTerm,
    status: newStatus,
    currency: newCurrency,
    country: newCountry,
  }: {
    searchTerm: string;
    status: SubAccountStatus;
    currency: string;
    country: string;
  }) => {
    setPageIndex(0);

    updateQueryParams({
      status: newStatus || 'all',
      currency: newCurrency,
      country: newCountry,
      searchTerm: newSearchTerm,
    });
  };

  // Re-run the list fetch so a row mutation (block / unblock / deactivate / edit) is reflected. Throws on
  // !Success like every Direct-Pattern fetch; the callers handle it. Sends every active filter, Country included
  // (the old page dropped Country on its block / unblock re-fetches, un-filtering the list).
  const refetchSubAccounts = () =>
    fetchSubAccounts({
      PortalUser: account.accountID,
      ShowToken: true,
      SearchText: searchTerm || undefined,
      ActiveOnly: status === 'Active' ? true : status === 'NotActive' ? false : undefined,
      Currency: currency || undefined,
      Country: country || undefined,
      Limit: 20,
      Offset: offset,
    });

  async function handleBlockAccount(accountID: string, accountName: string) {
    const confirmed = await dialogue.open<true>(
      (props) => <BlockAccountDialog {...props} accountName={accountName} />,
      { bare: true },
    );

    if (!confirmed) return;

    try {
      blockingLoader.show();
      await blockAccount(accountID);
      await refetchSubAccounts();
      toast.neutral(t('blockAccountSuccessToast', { accountName, defaultValue: '{{accountName}} has been blocked' }));
    } catch (error) {
      // The hook already logged; the block flow must acknowledge the failure, so use the error dialog.
      if (error instanceof Error) alertDialog.error(t('errorTitle', { defaultValue: 'Error' }), error.message);
    } finally {
      blockingLoader.hide();
    }
  }

  async function handleUnblockAccount(accountID: string, accountName: string) {
    const confirmed = await dialogue.open<true>(
      (props) => <UnBlockAccountDialog {...props} accountName={accountName} />,
      { bare: true },
    );

    if (!confirmed) return;

    try {
      blockingLoader.show();
      await unblockAccount(accountID);
      await refetchSubAccounts();

      toast.neutral(
        t('unblockAccountSuccessToast', { accountName, defaultValue: '{{accountName}} has been unblocked' }),
      );
    } catch (error) {
      // The hook already logged; the unblock flow must acknowledge the failure, so use the error dialog.
      if (error instanceof Error) alertDialog.error(t('errorTitle', { defaultValue: 'Error' }), error.message);
    } finally {
      blockingLoader.hide();
    }
  }

  async function handleBlockAllAccounts() {
    const confirmed = await dialogue.open(BlockDialog, { bare: true });

    if (!confirmed) return;

    try {
      blockingLoader.show();
      await blockAccounts();
      await refetchSubAccounts();
      toast.neutral(t('blockSuccessToast', { defaultValue: 'All Sub-Accounts have been blocked' }));
    } catch (error) {
      // The hook already logged; a failed block-all is a non-blocking toast, as on the old page.
      if (error instanceof Error) toast.neutral(error.message);
    } finally {
      blockingLoader.hide();
    }
  }

  async function handleUnblockAllAccounts() {
    const confirmed = await dialogue.open(UnBlockDialog, { bare: true });

    if (!confirmed) return;

    try {
      blockingLoader.show();
      await unblockAccounts();
      await refetchSubAccounts();
      toast.neutral(t('unblockSuccessToast', { defaultValue: 'All Sub-Accounts have been unblocked' }));
    } catch (error) {
      // The hook already logged; a failed unblock-all is a non-blocking toast, as on the old page.
      if (error instanceof Error) toast.neutral(error.message);
    } finally {
      blockingLoader.hide();
    }
  }

  // Shared by the table's flat view and allAccountsBlocked below, so the balance-flattening work happens
  // once per fetch instead of twice.
  const mappedSubAccounts = useMemo(() => mapBalances(subAccounts), [subAccounts]);

  // Determine if all accounts are blocked/locked
  const allAccountsBlocked = useMemo(() => {
    if (mappedSubAccounts.length === 0) return false;

    return mappedSubAccounts.every((subAccount) => subAccount.AccountStatus === 'Blocked');
  }, [mappedSubAccounts]);

  const state: SubAccountListState = {
    allAccountsBlocked,
    blockAccount: handleBlockAccount,
    unblockAccount: handleUnblockAccount,
    blockAllAccounts: handleBlockAllAccounts,
    unblockAllAccounts: handleUnblockAllAccounts,
  };

  const value: SubAccountListContextValue = {
    ...state,
    account,
    userPermissions,
    hostPaths,
    hostActions,
    permissionsEditor,
    filters: { status, currency, country, searchTerm },
    search,
    pagination: { pageCount, pageIndex, setPageIndex },
    subAccounts,
    mappedSubAccounts,
    isLoadingSubAccounts,
    refetchSubAccounts,
  };

  return <SubAccountListProvider value={value}>{children(state)}</SubAccountListProvider>;
}

// The compound part the host mounts where the list belongs (expando on the function declaration, so the
// root keeps the `export default function` form and react-refresh still sees a named component).
SubAccountList.Table = SubAccountListTable;
